import React, { useState } from 'react';
import { ArrowLeft, ArrowRight, CheckCircle2, Lightbulb, MessageSquareText } from 'lucide-react';

import { Button } from 'components/ui/button';
import { Card, CardContent, CardTitle } from 'components/ui/card';
import { Textarea } from 'components/ui/textarea';
import type { RoleplaySessionController } from './useRoleplaySession';
import { ScorecardDebrief, type ScorecardAction } from './scorecard/ScorecardDebrief';
import { isRenderableScorecard } from './scorecard/scorecardModel';

export type RoleplayPracticeFocus = { key: string; label: string; next: string };

const nonBlankStrings = (value: unknown): string[] =>
  (Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string' && Boolean(item.trim()))
    : []);

export const DebriefScreen: React.FC<{
  controller: RoleplaySessionController;
  onContinue: () => void;
  onExit: () => void;
  onBack?: () => void;
  /** Keep Continue available after an unsuccessful retry when the step was already completed. */
  canContinue?: boolean;
  continueLabel?: string;
  /** Start another attempt; a focus names the scorecard skill to work on. */
  onPracticeAgain?: (focus: RoleplayPracticeFocus | null) => void;
}> = ({ controller, onContinue, onExit, onBack, canContinue = false, continueLabel, onPracticeAgain }) => {
  const [reflection, setReflection] = useState(controller.session?.reflection ?? '');
  const scenario = controller.scenario;
  const session = controller.session;
  if (!scenario || !session) return null;
  const debrief = session.debrief ?? {};
  const evaluation = session.win_evaluation ?? {};
  const evaluationRationale = typeof evaluation.rationale === 'string'
    ? evaluation.rationale.trim()
    : '';
  const evaluationEvidence = nonBlankStrings(evaluation.evidence);
  const strengths = nonBlankStrings(debrief.strengths);
  const nextSteps = nonBlankStrings(debrief.next_steps);
  const transcriptEvidence = nonBlankStrings(debrief.evidence);
  const summary = typeof debrief.summary === 'string' ? debrief.summary.trim() : '';
  const revealed = Boolean(
    summary
    || strengths.length
    || transcriptEvidence.length
    || nextSteps.length,
  );
  const successful = session.status === 'completed' && session.completion_reason === 'win_condition_met';
  const resolvedContinueLabel = continueLabel ?? 'Continue course';
  const title = successful ? 'You completed the practice' : 'Let’s look back';
  const subtitle = successful
    ? 'Reflect on what helped the conversation reach a constructive stopping point.'
    : 'This attempt did not yet meet the practice goal. Review what was missing, then try again when you are ready.';
  const hasTakeaways = strengths.length > 0 || nextSteps.length > 0;
  const practiceAgain = (focus: RoleplayPracticeFocus | null = null) => {
    if (onPracticeAgain) onPracticeAgain(focus);
    else void controller.prepareRetry();
  };
  const scorecard = revealed && isRenderableScorecard(debrief.scorecard) ? debrief.scorecard : null;

  if (scorecard) {
    const retry: ScorecardAction = {
      label: successful ? 'Practice again' : 'Try again',
      onClick: () => practiceAgain(),
      testId: 'roleplay-run-again',
      disabled: controller.processing,
    };
    const continueAction: ScorecardAction = { label: resolvedContinueLabel, onClick: onContinue, testId: 'roleplay-done' };
    const [primaryAction, secondaryAction] = successful
      ? [continueAction, retry]
      : [retry, canContinue ? continueAction : { label: 'Leave practice', onClick: onExit, testId: 'roleplay-exit' }];
    return (
      <div className="rp-debrief-shell flex min-h-screen flex-col items-center px-4 pb-12 pt-7 lg:px-0 lg:pt-0" data-testid="roleplay-debrief">
        <ScorecardDebrief
          scenario={scenario}
          turns={session.turns}
          endExpression={session.current_expression}
          scorecard={scorecard}
          summary={summary}
          successful={successful}
          primaryAction={primaryAction}
          secondaryAction={secondaryAction}
          onPracticeSkill={(focus) => practiceAgain(focus)}
          practiceDisabled={controller.processing}
          onBack={onBack}
        />
      </div>
    );
  }

  return (
    <div className="rp-debrief-shell flex min-h-screen flex-col items-center gap-4 px-4 pb-12 pt-8" data-testid="roleplay-debrief">
      {onBack ? (
        <div className="w-full max-w-[40rem]">
          <Button variant="ghost" size="auto" onClick={onBack} data-testid="roleplay-back">
            <ArrowLeft size={16} /> Back
          </Button>
        </div>
      ) : null}
      <Card variant="glass" disableAnimation maxWidth="large" className="max-w-[40rem]">
        <CardTitle title={title} subtitle={subtitle} />
        <CardContent variant="form">
          {!revealed ? (
            <>
              <p className="type-body-m text-foundation-base-darkbase-dark-500">What did you notice about {scenario.persona.name}? What would you repeat or change next time?</p>
              <Textarea value={reflection} onChange={(event) => setReflection(event.target.value)} rows={5} placeholder="Write a few sentences…" className="rounded-xl border-surface-sky-end bg-white/80 text-[0.9375rem] leading-normal" data-testid="roleplay-reflection" />
              {controller.error && <p role="alert" className="type-body-s text-destructive">{controller.error}</p>}
              <Button onClick={() => void controller.createDebrief(reflection)} disabled={controller.processing} size="auto" data-testid="roleplay-reveal">
                {controller.processing ? 'Preparing feedback…' : 'Show my feedback'} <ArrowRight size={18} />
              </Button>
              <Button variant="secondary" onClick={() => practiceAgain()} disabled={controller.processing} size="auto" data-testid="roleplay-run-again">
                Practice again
              </Button>
            </>
          ) : (
            <div className="flex flex-col gap-3" data-testid="roleplay-results">
              {/* One-glance outcome: status plus the single-sentence summary. */}
              <section
                className={`flex items-start gap-3 rounded-xl px-4 py-3 ${successful ? 'bg-surface-success-soft' : 'bg-foundation-orange-50'}`}
                data-testid="roleplay-outcome"
              >
                {successful
                  ? <CheckCircle2 size={20} aria-hidden="true" className="mt-0.5 shrink-0 text-foundation-base-darkbase-dark-700" />
                  : <Lightbulb size={20} aria-hidden="true" className="mt-0.5 shrink-0 text-foundation-base-darkbase-dark-700" />}
                <div className="min-w-0">
                  <p className="type-body-s-semibold text-foundation-base-darkbase-dark-700">
                    {successful ? 'Goal met' : 'Goal not met yet'}
                  </p>
                  {summary && (
                    <p className="type-body-s mt-0.5 text-foundation-base-darkbase-dark-600">{summary}</p>
                  )}
                </div>
              </section>

              {/* The two things to remember, side by side on wider screens. */}
              {hasTakeaways && (
                <div className={`grid gap-3 ${strengths.length > 0 && nextSteps.length > 0 ? 'md:grid-cols-2' : ''}`}>
                  {strengths.length > 0 && (
                    <section className="rounded-xl bg-white/60 px-4 py-3" data-testid="roleplay-strengths">
                      <p className="type-body-s-semibold mb-2 text-foundation-base-darkbase-dark-700">What worked</p>
                      <ul className="space-y-1.5">
                        {strengths.map((strength) => (
                          <li key={strength} className="flex items-start gap-2 text-sm text-foundation-base-darkbase-dark-500">
                            <CheckCircle2 size={14} aria-hidden="true" className="mt-1 shrink-0 text-foundation-base-darkbase-dark-500" />
                            <span>{strength}</span>
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}
                  {nextSteps.length > 0 && (
                    <section className="rounded-xl bg-foundation-orange-50 px-4 py-3" data-testid="roleplay-next-steps">
                      <p className="type-body-s-semibold mb-2 text-foundation-base-darkbase-dark-700">Try next time</p>
                      <ul className="space-y-1.5">
                        {nextSteps.map((step) => (
                          <li key={step} className="flex items-start gap-2 text-sm text-foundation-base-darkbase-dark-500">
                            <ArrowRight size={14} aria-hidden="true" className="mt-1 shrink-0 text-foundation-base-darkbase-dark-500" />
                            <span>{step}</span>
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}
                </div>
              )}

              {/* The evaluator's reasoning, with its supporting quotes set apart. */}
              {(evaluationRationale || evaluationEvidence.length > 0) && (
                <section className="rounded-xl border border-foundation-calm-blue-200 bg-foundation-calm-blue-50 px-4 py-3" data-testid="roleplay-evaluator-explanation">
                  <p className="type-body-s-semibold mb-1 text-foundation-base-darkbase-dark-700">
                    {successful ? 'Why this met the goal' : 'What was missing'}
                  </p>
                  {evaluationRationale && (
                    <p className="type-body-s text-foundation-base-darkbase-dark-600">{evaluationRationale}</p>
                  )}
                  {evaluationEvidence.length > 0 && (
                    <ul className="mt-2 space-y-1.5">
                      {evaluationEvidence.map((item) => (
                        <li key={item}>
                          <blockquote className="border-l-2 border-foundation-calm-blue-200 pl-3 text-sm italic text-foundation-base-darkbase-dark-500">
                            {item}
                          </blockquote>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              )}

              {/* The full transcript evidence is the longest part; keep it one tap away. */}
              {transcriptEvidence.length > 0 && (
                <details className="group rounded-xl bg-white/60 px-4 py-3" data-testid="roleplay-transcript-evidence">
                  <summary className="flex cursor-pointer list-none items-center gap-2 type-body-s-semibold text-foundation-base-darkbase-dark-700 [&::-webkit-details-marker]:hidden">
                    <MessageSquareText size={16} aria-hidden="true" className="shrink-0" />
                    <span className="flex-1">Evidence from the conversation</span>
                    <span className="type-body-s font-normal text-foundation-base-darkbase-dark-400 group-open:hidden">Show</span>
                    <span className="type-body-s hidden font-normal text-foundation-base-darkbase-dark-400 group-open:inline">Hide</span>
                  </summary>
                  <ul className="mt-3 space-y-2">
                    {transcriptEvidence.map((item) => (
                      <li key={item} className="border-l-2 border-white/80 pl-3 text-sm text-foundation-base-darkbase-dark-500">
                        {item}
                      </li>
                    ))}
                  </ul>
                </details>
              )}

              <div className="flex flex-wrap gap-2.5 pt-1">
                {successful ? (
                  <>
                    <Button onClick={onContinue} size="auto" data-testid="roleplay-done">{resolvedContinueLabel}</Button>
                    <Button variant="secondary" onClick={() => practiceAgain()} disabled={controller.processing} size="auto" data-testid="roleplay-run-again">Practice again</Button>
                  </>
                ) : canContinue ? (
                  <>
                    <Button onClick={() => practiceAgain()} disabled={controller.processing} size="auto" data-testid="roleplay-run-again">Practice again</Button>
                    <Button variant="secondary" onClick={onContinue} size="auto" data-testid="roleplay-done">{resolvedContinueLabel}</Button>
                  </>
                ) : (
                  <>
                    <Button onClick={() => practiceAgain()} disabled={controller.processing} size="auto" data-testid="roleplay-run-again">Practice again</Button>
                    <Button variant="ghost" onClick={onExit} size="auto" data-testid="roleplay-exit">Leave practice</Button>
                  </>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
